import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import z from 'zod';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import {
    createZodFormResolver,
    getDefaults,
    LocalizedText,
    logSentryError,
    showSuccessAlert,
    useFormControls,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { EnterpriseProfileOptions, LearningPath, useRepositoryMutation } from '@mews-commander/data-access';
import { EnterpriseProfilePrivilegesEnumNames } from '@mews-commander/legacy-data-structures';
import { isPrivilegeValue } from '@mews-commander/legacy-entities/src/utils';
import { canManageRoles } from '../../utils';
import { useUserType } from '../../hooks/useUserType';
import { useDefaultSuperiorProfileId } from '../../hooks/useCommonFormOptions';
import { RolesAndPermissionsOption } from '../../types';
import { FieldNames } from '../FieldNames';

const commonSchema = z.object({
    [FieldNames.Email]: z.string().email().default(''),
    [FieldNames.AssignedLearningPath]: z.nativeEnum(LearningPath).refine((value) => value),
    [FieldNames.Privileges]: z
        .array(z.custom<EnterpriseProfilePrivilegesEnumNames>(isPrivilegeValue))
        .optional()
        .default([]),
    [FieldNames.ReceiveCustomerMessages]: z.boolean().optional().default(false),
    [FieldNames.Reset2FA]: z.boolean().optional().default(false),
    [FieldNames.RoleIds]: z.array(z.string()).optional().default([]),
    [FieldNames.RolesAndPermissions]: z.nativeEnum(RolesAndPermissionsOption).default(RolesAndPermissionsOption.Roles),
    [FieldNames.SuperiorProfileId]: z.string().optional().default(''),
    [FieldNames.DepartmentId]: z.string().optional().default(''),
    [FieldNames.Cashiers]: z.array(z.string()).optional().default([]),
    [FieldNames.EmployeeIncentiveCode]: z.string().optional().default(''),
});

const schema = z.discriminatedUnion(FieldNames.RolesAndPermissions, [
    commonSchema.extend({
        [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.Roles),
        [FieldNames.RoleIds]: z.array(z.string()).min(1),
    }),
    commonSchema.extend({
        [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.CustomizedPermissions),
        [FieldNames.Privileges]: z.array(z.custom<EnterpriseProfilePrivilegesEnumNames>(isPrivilegeValue)).min(1),
    }),
    commonSchema.extend({
        [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.Admin),
    }),
    commonSchema.extend({
        [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.NoPermissions),
    }),
]);

export type AddUserFormValues = z.infer<typeof schema>;
const resolver = createZodFormResolver(schema);

export const useAddUserForm = ({ onSuccess }: { onSuccess: () => void }) => {
    const enterpriseId = useCurrentEnterpriseId();
    const dispatch = useDispatch();
    const isRoleManagementEnabled = useSelector(canManageRoles);
    const defaultSuperiorProfileId = useDefaultSuperiorProfileId();
    const { isPortfolioCentralUser } = useUserType();

    const addProfileMutation = useRepositoryMutation('enterpriseProfiles', 'add', {
        invalidateQueries: { userProfiles: ['getAll'] },
    });
    const addCashiersMutation = useRepositoryMutation('cashierAssignments', 'add');

    const defaultValues = useMemo(() => {
        const rolesAndPermissionsDefault = isRoleManagementEnabled
            ? RolesAndPermissionsOption.Roles
            : RolesAndPermissionsOption.NoPermissions;

        const defaults = getDefaults(commonSchema);

        return {
            ...defaults,
            [FieldNames.RolesAndPermissions]: isPortfolioCentralUser
                ? RolesAndPermissionsOption.Admin
                : rolesAndPermissionsDefault,
            [FieldNames.SuperiorProfileId]: defaultSuperiorProfileId,
        };
    }, [defaultSuperiorProfileId, isPortfolioCentralUser, isRoleManagementEnabled]);

    const handleSubmit = async (formData: AddUserFormValues) => {
        try {
            const data = await addProfileMutation.mutateAsync(parseFormDataToPayload(formData));

            if (formData[FieldNames.Cashiers]?.length && data.profiles?.[0].id) {
                const assignments = formData[FieldNames.Cashiers].map((cashierId) => ({
                    cashierId,
                    profileId: data.profiles?.[0].id,
                }));

                await addCashiersMutation.mutateAsync({ assignments, enterpriseId });
            }

            showSuccessAlert({
                description: <LocalizedText textKey={TextKey.SaveSuccessMessage} />,
            });

            onSuccess();
        } catch (error) {
            dispatch(logSentryError({ error }));
        }
    };

    const otherMethods = useFormControls({
        onSubmit: handleSubmit,
        resolver,
        defaultValues,
    });

    return {
        ...otherMethods,
        isPending: addProfileMutation.isPending || addCashiersMutation.isPending,
    };
};

const parseFormDataToPayload = (formData: AddUserFormValues) => {
    const {
        assignedLearningPath,
        rolesAndPermissions,
        email,
        roleIds,
        receiveCustomerMessages,
        reset2FA,
        departmentId,
        cashiers,
        superiorProfileId,
        employeeIncentiveCode,
    } = formData;

    const options: EnterpriseProfileOptions = {
        receiveCustomerMessages,
        resetUsersTwoFactorAuthentication: reset2FA,
    };

    const privileges = formData[FieldNames.Privileges]?.length
        ? pipe(
              formData[FieldNames.Privileges],
              array.map((privilege) => [privilege, true]),
              Object.fromEntries
          )
        : undefined;

    return {
        assignedLearningPath,
        departmentId: departmentId || null, // if it's "", use null
        email,
        isAdmin: rolesAndPermissions === RolesAndPermissionsOption.Admin,
        options,
        privileges,
        roleIds: roleIds?.length ? roleIds : null,
        cashiers,
        superiorProfileId,
        employeeIncentiveCode: employeeIncentiveCode || null,
    };
};
